import { TextInput, Stack, Select, Group, Switch, MultiSelect } from '@mantine/core';
import { Formik, Form } from 'formik';
import { SendEmailButton } from '../buttons/SendEmailButton';
import { GenerateTestMessageButton } from '../buttons/GenerateTestMessageButton';
import { RichTextEditor } from '../../../components/RichTextEditor';
import { useContacts } from '../../contacts/hooks/useContacts';
import { useMemo } from 'react';

interface ComposeEmailFormValues {
    aliasId: string;
    to: string;
    subject: string;
    htmlBody: string;
    useHtml: boolean;
}

interface ComposeEmailFormProps {
    initialValues: ComposeEmailFormValues;
    aliasOptions: { value: string; label: string }[];
    onSubmit: (values: ComposeEmailFormValues) => Promise<void>;
    generateTestMessage: (setFieldValue: (field: string, value: any) => void, currentAliasId: string) => void;
}

export const ComposeEmailForm = ({ initialValues, aliasOptions, onSubmit, generateTestMessage }: ComposeEmailFormProps) => {
    const { visibleItems: contacts } = useContacts();

    const contactOptions = useMemo(() => {
        // Options are keyed on the email address (the MultiSelect value), so two
        // contacts sharing an email would emit duplicate option values — Mantine
        // throws "Duplicate options are not supported" rather than dedupe. Collapse
        // to one option per unique email, keeping the first contact's label.
        const seen = new Set<string>();
        return contacts
            .map(contact => ({
                value: contact.email || '',
                label: `${contact.firstName || ''} ${contact.lastName || ''} <${contact.email || ''}>`
            }))
            .filter(option => {
                if (!option.value || seen.has(option.value)) {
                    return false;
                }
                seen.add(option.value);
                return true;
            });
    }, [contacts]);

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            enableReinitialize
        >
            {({ values, setFieldValue, submitForm }) => (
                <Form>
                    <Stack>
                        <Select
                            label="From Alias"
                            placeholder="Select an alias"
                            data={aliasOptions}
                            value={values.aliasId}
                            onChange={(value) => setFieldValue('aliasId', value)}
                            required
                        />
                        <MultiSelect
                            label="To"
                            placeholder="Select contacts or type email addresses"
                            data={contactOptions}
                            value={values.to ? values.to.split(',').map(e => e.trim()) : []}
                            onChange={(selectedValues) => setFieldValue('to', selectedValues.join(', '))}
                            searchable
                            required
                        />
                        <TextInput
                            label="Subject"
                            placeholder="Email subject"
                            name="subject"
                            value={values.subject}
                            onChange={(e) => setFieldValue('subject', e.target.value)}
                            required
                        />
                        <Switch
                            label="Use HTML Editor"
                            checked={values.useHtml}
                            onChange={(event) => setFieldValue('useHtml', event.currentTarget.checked)}
                        />
                        {values.useHtml ? (
                            <RichTextEditor
                                value={values.htmlBody}
                                onChange={(html) => setFieldValue('htmlBody', html)}
                                placeholder="Email body"
                            />
                        ) : (
                            <textarea
                                placeholder="Email body (plain text)"
                                value={values.htmlBody}
                                onChange={(e) => setFieldValue('htmlBody', e.target.value)}
                                style={{ minHeight: '200px', width: '100%', padding: '8px' }}
                            />
                        )}
                        <Group>
                            <SendEmailButton
                                onClick={submitForm}
                                disabled={!values.aliasId || !values.to || !values.subject}
                            />
                            <GenerateTestMessageButton
                                onClick={() => generateTestMessage(setFieldValue, values.aliasId)}
                            />
                        </Group>
                    </Stack>
                </Form>
            )}
        </Formik>
    );
};
